import './index.css';
import { GameInstance, BattleShipGameState } from '../../../../types/types';
import useBattleShipGamePage from '../../../../hooks/useBattleShipGamePage';
import BattleShipBoard from './board';

/**
 * BattleshipGamePage component renders the Battleship game page.
 *
 * @param param0 - The game instance containing the state and methods for the Battleship game.
 * @param param0.gameInstance - The game instance containing the state and methods for the Battleship game.
 * @returns - The batthership game page component.
 */
const BattleshipGamePage = ({
  gameInstance,
}: {
  gameInstance: GameInstance<BattleShipGameState>;
}) => {
  const { user, opponentUsername, handleCellClick, handleMakeMove, handleResetGame } =
    useBattleShipGamePage(gameInstance);

  const playerBoard = gameInstance.state.boards[user.username];
  const opponentBoard = gameInstance.state.boards[opponentUsername];

  return (
    <>
      <div className='battleship-rules'>
        <h2>Rules of Battleship</h2>
        <p>The game of Battleship is played as follows:</p>
        <ol>
          <li>Each player places ships on a grid without revealing them to the opponent.</li>
          <li>Players take turns selecting coordinates to fire at.</li>
          <li>The goal is to sink all of your opponent&apos;s ships.</li>
        </ol>
      </div>

      <div className='battleship-game-details'>
        <h2>Current Game</h2>
        <p>
          <strong>Player 1:</strong> {gameInstance.state.player1 || 'Waiting...'}
        </p>
        <p>
          <strong>Player 2:</strong> {gameInstance.state.player2 || 'Waiting...'}
        </p>
        {/* if in progress show current turn */}
        {gameInstance.state.status === 'IN_PROGRESS' && (
          <div className='current-turn'>
            <h3>Current Turn: {gameInstance.state.currentTurn}</h3>
          </div>
        )}

        {gameInstance.state.status === 'OVER' && (
          <p>
            <strong>Winner:</strong> {gameInstance.state.winners?.join(', ') || 'No winner'}
          </p>
        )}

        {gameInstance.state.status === 'IN_PROGRESS' && (
          <div className='battleship-boards-container'>
            <div className='player-side'>
              <h3>Your Board</h3>
              <BattleShipBoard board={playerBoard} isPlayerBoard={true} />
            </div>

            <div className='opponent-side'>
              <h3>Opponent’s Board</h3>
              <BattleShipBoard
                board={opponentBoard}
                isPlayerBoard={false}
                onCellClick={(row, col) => handleCellClick(row, col)}
              />
            </div>
          </div>
        )}

        {/* Conditionally render Fire button only if game is in progress */}
        {gameInstance.state.status === 'IN_PROGRESS' && (
          <div className='battleship-action'>
            <button
              className='btn-submit'
              onClick={handleMakeMove}
              disabled={gameInstance.state.currentTurn !== user.username}>
              Fire
            </button>
          </div>
        )}
      </div>

      {/* show the final board */}
      {gameInstance.state.status === 'OVER' && (
        <div className='battleship-boards-final-container'>
          <h2>Final Board</h2>
          <BattleShipBoard board={opponentBoard} isPlayerBoard={false} />
        </div>
      )}

      {/* reset button */}
      <div className='battleship-action'>
        <button className='btn-submit' onClick={handleResetGame}>
          Reset Game
        </button>
      </div>
    </>
  );
};

export default BattleshipGamePage;
